/**
 * Récords por ejercicio, 1RM estimado y progreso en el tiempo (Req. 25;
 * design.md, 8.13).
 *
 * - Las series de calentamiento no cuentan para nada (Req. 25.5), ni las que
 *   no se marcaron como hechas.
 * - El 1RM estimado es la fórmula de Epley y solo se usa de 1 a 12
 *   repeticiones (Req. 25.2): fuera de ese rango la estimación ya no sirve, y
 *   la app prefiere no dar un número a dar uno malo.
 * - Las series con la carga en placas no entran en peso, 1RM ni volumen: el
 *   número de placa de una máquina no es un peso ni se compara con otra
 *   máquina. Se cuentan aparte, como en el resumen del entrenamiento.
 * - Un récord es nuevo solo si supera estrictamente el mejor de las sesiones
 *   anteriores (tolerancia 1e-9). La primera vez con un ejercicio no es
 *   récord: no hay con qué comparar, y decir "récord" cada vez que se estrena
 *   un ejercicio sería ruido.
 * - Nada de esto se guarda: sale de las series cada vez, así que corregir una
 *   serie corrige el récord (Req. 13.2).
 *
 * Módulo puro: sin React y sin Dexie.
 */

import type { Modalidad, TipoEjercicio, WorkoutSet } from '@/db/types';
import { formatearCorto, type FechaLocal } from './dates';
import type { PuntoGrafica } from './grafica';
import { formatearReloj, repsDeSerie } from './entrenamiento';
import { formatearNumero } from './numbers';
import { desdeKg, type UnidadPeso } from './units';

/** La fórmula de Epley solo se usa en este rango de repeticiones (Req. 25.2). */
export const REPS_MIN_RM = 1;
export const REPS_MAX_RM = 12;

/** Dos valores iguales no son un récord nuevo; el margen cubre el redondeo. */
export const TOLERANCIA = 1e-9;

/**
 * 1RM estimado con la fórmula de Epley: peso × (1 + reps / 30), y el peso tal
 * cual con una sola repetición. null fuera de 1 a 12 repeticiones o sin carga
 * en kg (por ejemplo en placas).
 */
export function epley(pesoKg: number | null, reps: number | null): number | null {
  if (pesoKg === null || !Number.isFinite(pesoKg) || pesoKg <= 0) return null;
  if (reps === null || !Number.isInteger(reps) || reps < REPS_MIN_RM || reps > REPS_MAX_RM) return null;
  return reps === 1 ? pesoKg : pesoKg * (1 + reps / 30);
}

// ---------------------------------------------------------------------------
// Las métricas de cada tipo de ejercicio (Req. 25.3)
// ---------------------------------------------------------------------------

export type Metrica = 'peso' | 'rm' | 'volumen' | 'reps' | 'duracion' | 'distancia';

/** Qué récords lleva cada tipo de ejercicio (Req. 25.3). */
export const METRICAS_DE: Readonly<Record<TipoEjercicio, readonly Metrica[]>> = {
  peso_reps: ['peso', 'rm', 'volumen'],
  reps: ['reps'],
  tiempo: ['duracion'],
  distancia: ['distancia'],
};

export const NOMBRE_METRICA: Readonly<Record<Metrica, string>> = {
  peso: 'Peso máximo',
  rm: 'Mejor 1RM estimado',
  volumen: 'Mejor volumen de una sesión',
  reps: 'Más repeticiones en una serie',
  duracion: 'Más tiempo en una serie',
  distancia: 'Más distancia en una serie',
};

/** Para el selector de la gráfica, donde el espacio es poco. */
export const CORTO_METRICA: Readonly<Record<Metrica, string>> = {
  peso: 'Peso',
  rm: '1RM',
  volumen: 'Volumen',
  reps: 'Repeticiones',
  duracion: 'Tiempo',
  distancia: 'Distancia',
};

/** Dentro de una frase: "rompiste tu peso máximo". */
export const EN_FRASE_METRICA: Readonly<Record<Metrica, string>> = {
  peso: 'peso máximo',
  rm: '1RM estimado',
  volumen: 'volumen de la sesión',
  reps: 'repeticiones en una serie',
  duracion: 'tiempo en una serie',
  distancia: 'distancia en una serie',
};

export const NOMBRE_LARGO_METRICA: Readonly<Record<Metrica, string>> = {
  peso: 'Peso máximo de una serie',
  rm: '1RM estimado',
  volumen: 'Volumen de la sesión',
  reps: 'Repeticiones de una serie',
  duracion: 'Tiempo de una serie',
  distancia: 'Distancia de una serie',
};

/** De dónde sale cada número, en una línea (Req. 8.11: cada número se explica). */
export const COMO_SALE_METRICA: Readonly<Record<Metrica, string>> = {
  peso: 'La carga más alta de una serie de trabajo.',
  rm: 'Lo máximo que levantarías una vez, estimado con Epley: carga × (1 + repeticiones ÷ 30), de 1 a 12 repeticiones.',
  volumen: 'Carga × repeticiones de todas las series de trabajo de esa sesión.',
  reps: 'Las repeticiones de la mejor serie de trabajo; por lado, las de un solo lado.',
  duracion: 'La serie de trabajo más larga.',
  distancia: 'La serie de trabajo más larga en distancia.',
};

/** En qué unidad queda cada métrica: así la pantalla sabe cómo presentarla. */
export const UNIDAD_METRICA: Readonly<Record<Metrica, 'carga' | 'reps' | 'tiempo' | 'distancia'>> = {
  peso: 'carga',
  rm: 'carga',
  volumen: 'carga',
  reps: 'reps',
  duracion: 'tiempo',
  distancia: 'distancia',
};

// ---------------------------------------------------------------------------
// Lo que estos cálculos leen
// ---------------------------------------------------------------------------

export type SerieDeRecord = Pick<
  WorkoutSet,
  'tipoSerie' | 'pesoKg' | 'pesoCapturado' | 'reps' | 'duracionSeg' | 'distanciaM' | 'hecha'
> &
  Partial<Pick<WorkoutSet, 'repsIzq' | 'repsDer'>>;

export interface SesionDeRecords {
  workoutId: string;
  fecha: FechaLocal;
  /** Instante de inicio: ordena dos sesiones del mismo día. */
  inicio: string;
  /** Gimnasio o casa; ausente = gimnasio. Los récords cuentan las dos. */
  modalidad?: Modalidad;
  series: readonly SerieDeRecord[];
}

/** Las series que cuentan: hechas y de trabajo (Req. 25.5). */
export function seriesQueCuentan(series: readonly SerieDeRecord[]): SerieDeRecord[] {
  return series.filter((s) => s.hecha && s.tipoSerie !== 'calentamiento');
}

/**
 * Las repeticiones de una serie para comparar entre días: en las anotadas por
 * lado, las del mejor lado, no la suma. 10 izquierda y 10 derecha son 10
 * repeticiones, no 20: si no, un ejercicio por lado parecería el doble.
 */
export function repsParaComparar(s: SerieDeRecord): number | null {
  const izq = s.repsIzq ?? null;
  const der = s.repsDer ?? null;
  if (izq !== null || der !== null) {
    const mejor = Math.max(izq ?? 0, der ?? 0);
    return mejor > 0 ? mejor : null;
  }
  return s.reps ?? null;
}

export type ValoresDeSesion = Readonly<Record<Metrica, number | null>>;

export interface MedidasDeSesion {
  workoutId: string;
  fecha: FechaLocal;
  inicio: string;
  modalidad?: Modalidad;
  /** El mejor valor de cada métrica en esa sesión; null si esa sesión no lo tiene. */
  valores: ValoresDeSesion;
  /** Series de trabajo hechas de ese ejercicio. */
  seriesDeTrabajo: number;
  /** De esas, las que tienen la carga en placas: no entran en peso, 1RM ni volumen. */
  enPlacas: number;
}

const mayor = (actual: number | null, valor: number | null): number | null => {
  if (valor === null || !Number.isFinite(valor)) return actual;
  return actual === null || valor > actual ? valor : actual;
};

/** Lo mejor de cada métrica en una sesión, con el volumen sumado. */
export function medidasDeSesion(sesion: SesionDeRecords): MedidasDeSesion {
  const cuentan = seriesQueCuentan(sesion.series);
  let peso: number | null = null;
  let rm: number | null = null;
  let reps: number | null = null;
  let duracion: number | null = null;
  let distancia: number | null = null;
  let volumen = 0;
  let conCarga = false;
  let enPlacas = 0;
  for (const s of cuentan) {
    if (s.pesoCapturado?.unidad === 'placa') enPlacas += 1;
    const repsComparables = repsParaComparar(s);
    peso = mayor(peso, s.pesoKg);
    rm = mayor(rm, epley(s.pesoKg, repsComparables));
    reps = mayor(reps, repsComparables);
    duracion = mayor(duracion, s.duracionSeg);
    distancia = mayor(distancia, s.distanciaM);
    if (s.pesoKg !== null && Number.isFinite(s.pesoKg)) {
      // El volumen sí cuenta los dos lados: es todo lo que se movió
      volumen += s.pesoKg * repsDeSerie(s);
      conCarga = true;
    }
  }
  return {
    workoutId: sesion.workoutId,
    fecha: sesion.fecha,
    inicio: sesion.inicio,
    ...(sesion.modalidad === undefined ? {} : { modalidad: sesion.modalidad }),
    valores: { peso, rm, volumen: conCarga && volumen > 0 ? volumen : null, reps, duracion, distancia },
    seriesDeTrabajo: cuentan.length,
    enPlacas,
  };
}

/** De la más antigua a la más reciente; a la misma hora, en el orden en que llegaron. */
function enOrden(sesiones: readonly SesionDeRecords[]): MedidasDeSesion[] {
  return sesiones.map(medidasDeSesion).sort((a, b) => a.inicio.localeCompare(b.inicio));
}

// ---------------------------------------------------------------------------
// Récords (Req. 25.3) y récords nuevos (Req. 25.4)
// ---------------------------------------------------------------------------

export interface RecordDeEjercicio {
  metrica: Metrica;
  valor: number;
  fecha: FechaLocal;
  workoutId: string;
  modalidad?: Modalidad;
  /** Cuántas sesiones entraron en la comparación. */
  sesiones: number;
}

/**
 * El mejor valor de cada métrica del tipo de ejercicio, con la sesión donde
 * se logró (Req. 25.3). Si se repite el mismo valor, queda la primera vez que
 * se logró: igualar no es superar.
 */
export function recordsDeEjercicio(sesiones: readonly SesionDeRecords[], tipo: TipoEjercicio): RecordDeEjercicio[] {
  const medidas = enOrden(sesiones);
  const records: RecordDeEjercicio[] = [];
  for (const metrica of METRICAS_DE[tipo]) {
    let mejor: MedidasDeSesion | null = null;
    let valor: number | null = null;
    for (const m of medidas) {
      const v = m.valores[metrica];
      if (v === null) continue;
      if (valor === null || v > valor + TOLERANCIA) {
        valor = v;
        mejor = m;
      }
    }
    if (mejor !== null && valor !== null) {
      records.push({
        metrica,
        valor,
        fecha: mejor.fecha,
        workoutId: mejor.workoutId,
        ...(mejor.modalidad === undefined ? {} : { modalidad: mejor.modalidad }),
        sesiones: medidas.length,
      });
    }
  }
  return records;
}

export interface RecordNuevo {
  metrica: Metrica;
  valor: number;
  /** El mejor de las sesiones anteriores, que acaba de superar. */
  anterior: number;
  /** Cuándo se había logrado el anterior. */
  anteriorFecha: FechaLocal;
}

/**
 * Los récords que una sesión acaba de romper (Req. 25.4): supera
 * estrictamente el mejor de las sesiones anteriores. Sin un valor anterior
 * con qué comparar no hay récord, así que la primera vez con un ejercicio no
 * se anuncia como récord.
 */
export function recordsNuevos(
  sesion: SesionDeRecords,
  anteriores: readonly SesionDeRecords[],
  tipo: TipoEjercicio,
): RecordNuevo[] {
  const hoy = medidasDeSesion(sesion);
  const antes = enOrden(anteriores.filter((s) => s.workoutId !== sesion.workoutId));
  const nuevos: RecordNuevo[] = [];
  for (const metrica of METRICAS_DE[tipo]) {
    const valor = hoy.valores[metrica];
    if (valor === null) continue;
    let anterior: number | null = null;
    let anteriorFecha: FechaLocal | null = null;
    for (const m of antes) {
      const v = m.valores[metrica];
      if (v === null) continue;
      if (anterior === null || v > anterior) {
        anterior = v;
        anteriorFecha = m.fecha;
      }
    }
    if (anterior === null || anteriorFecha === null) continue;
    if (valor > anterior + TOLERANCIA) nuevos.push({ metrica, valor, anterior, anteriorFecha });
  }
  return nuevos;
}

/** ¿Es la primera sesión con este ejercicio? Entonces no hay récords que anunciar. */
export function esLaPrimeraVez(sesion: SesionDeRecords, anteriores: readonly SesionDeRecords[]): boolean {
  return anteriores.every((s) => s.workoutId === sesion.workoutId);
}

// ---------------------------------------------------------------------------
// Progreso en el tiempo (Req. 25.1)
// ---------------------------------------------------------------------------

/**
 * Un punto por día con el mejor valor de esa métrica, en su unidad canónica
 * (kg, repeticiones, segundos o metros). Con dos sesiones el mismo día queda
 * la mejor: la gráfica lleva un punto por fecha.
 */
export function puntosDeMetrica(sesiones: readonly SesionDeRecords[], metrica: Metrica): PuntoGrafica[] {
  const porDia = new Map<FechaLocal, number>();
  for (const m of sesiones.map(medidasDeSesion)) {
    const v = m.valores[metrica];
    if (v === null) continue;
    const actual = porDia.get(m.fecha);
    if (actual === undefined || v > actual) porDia.set(m.fecha, v);
  }
  return [...porDia.entries()]
    .map(([fecha, valor]) => ({ fecha, valor }))
    .sort((a, b) => a.fecha.localeCompare(b.fecha));
}

/** Las métricas que de verdad tienen datos, para no ofrecer una gráfica vacía. */
export function metricasConDatos(sesiones: readonly SesionDeRecords[], tipo: TipoEjercicio): Metrica[] {
  const medidas = sesiones.map(medidasDeSesion);
  return METRICAS_DE[tipo].filter((metrica) => medidas.some((m) => m.valores[metrica] !== null));
}

// ---------------------------------------------------------------------------
// Presentación
// ---------------------------------------------------------------------------

/** El valor en la unidad que se muestra, para la gráfica y su eje. */
export function valorEnUnidad(metrica: Metrica, valorKg: number, unidad: UnidadPeso): number {
  if (UNIDAD_METRICA[metrica] !== 'carga') return valorKg;
  return desdeKg(valorKg, unidad);
}

/**
 * Un valor YA convertido a la unidad que se muestra, como se lee: "40 kg",
 * "2,450 kg", "12", "1:30", "2 km". Es lo que necesita la gráfica, que recibe
 * los puntos en la unidad preferida (Req. 5.7).
 */
export function textoMostrado(metrica: Metrica, valor: number, unidad: UnidadPeso): string {
  switch (UNIDAD_METRICA[metrica]) {
    case 'carga':
      return metrica === 'volumen'
        ? `${formatearNumero(Math.round(valor), { decimales: 0 })} ${unidad}`
        : `${formatearNumero(valor, { decimales: 2, fijos: false })} ${unidad}`;
    case 'reps':
      return formatearNumero(valor, { decimales: 0 });
    case 'tiempo':
      return formatearReloj(Math.round(valor));
    default:
      return `${formatearNumero(valor / 1000, { decimales: 2, fijos: false })} km`;
  }
}

/** El valor de una métrica, en kg o en su unidad canónica, como se lee. */
export function textoDeMetrica(metrica: Metrica, valorKg: number, unidad: UnidadPeso): string {
  return textoMostrado(metrica, valorEnUnidad(metrica, valorKg, unidad), unidad);
}

/** Un cambio con su signo: "+2.5 kg", "−1:30". */
export function textoDeCambio(metrica: Metrica, valor: number, unidad: UnidadPeso): string {
  const texto = textoMostrado(metrica, Math.abs(valor), unidad);
  if (valor === 0) return texto;
  return `${valor > 0 ? '+' : '\u2212'}${texto}`;
}

/** La unidad del eje de la gráfica de esa métrica. */
export function unidadDeMetrica(metrica: Metrica, unidad: UnidadPeso): string {
  switch (UNIDAD_METRICA[metrica]) {
    case 'carga':
      return unidad;
    case 'reps':
      return 'reps';
    case 'tiempo':
      return 'seg';
    default:
      return 'm';
  }
}

/** "peso máximo 40 kg (+2.5 kg que el 14 oct)": qué récord se rompió y por cuánto. */
export function textoDeRecordNuevo(r: RecordNuevo, unidad: UnidadPeso): string {
  const subida = textoDeCambio(r.metrica, valorEnUnidad(r.metrica, r.valor - r.anterior, unidad), unidad);
  return `${EN_FRASE_METRICA[r.metrica]} ${textoDeMetrica(r.metrica, r.valor, unidad)} (${subida} que el ${formatearCorto(
    r.anteriorFecha,
  )})`;
}
